import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';

// Confirmation Dialog Context
const ConfirmDialogContext = createContext();

/**
 * Confirmation Dialog Provider
 * Provides a reusable confirmation dialog throughout the app
 * 
 * Usage in App.js:
 * <ConfirmDialogProvider>
 *   <YourApp />
 * </ConfirmDialogProvider>
 */
export function ConfirmDialogProvider({ children }) {
  const [dialog, setDialog] = useState({
    open: false,
    title: 'Pengesahan',
    message: 'Adakah anda pasti?',
    type: 'warning', // 'warning', 'danger', 'info'
    confirmText: 'Ya',
    cancelText: 'Batal',
    onConfirm: null,
    onCancel: null,
  });

  /**
   * Show confirmation dialog
   * @param {Object} options - Dialog options
   * @returns {Promise} Promise that resolves to true (confirmed) or false (cancelled)
   */
  const showConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      setDialog({
        open: true,
        title: options.title || 'Pengesahan',
        message: options.message || 'Adakah anda pasti?',
        type: options.type || 'warning',
        confirmText: options.confirmText || 'Ya',
        cancelText: options.cancelText || 'Batal',
        onConfirm: () => {
          setDialog(prev => ({ ...prev, open: false }));
          resolve(true);
        },
        onCancel: () => {
          setDialog(prev => ({ ...prev, open: false }));
          resolve(false);
        },
      });
    });
  }, []);

  /**
   * Show delete confirmation
   * @param {string} itemName - Name of item to delete
   * @param {string} additionalInfo - Additional warning info
   */
  const confirmDelete = useCallback((itemName, additionalInfo = null) => {
    return showConfirm({
      title: 'Padam Rekod',
      message: `Adakah anda pasti ingin memadam ${itemName}?${
        additionalInfo ? `\n\n${additionalInfo}` : ''
      }\n\nTindakan ini tidak boleh dibatalkan.`,
      type: 'danger',
      confirmText: 'Padam',
      cancelText: 'Batal',
    });
  }, [showConfirm]);

  /**
   * Show save confirmation
   * @param {string} message - Confirmation message
   */
  const confirmSave = useCallback((message = 'Adakah anda pasti ingin menyimpan perubahan ini?') => {
    return showConfirm({
      title: 'Simpan Perubahan',
      message,
      type: 'info',
      confirmText: 'Simpan',
      cancelText: 'Batal',
    });
  }, [showConfirm]);

  /**
   * Show discard changes confirmation
   */
  const confirmDiscard = useCallback(() => {
    return showConfirm({
      title: 'Batalkan Perubahan',
      message: 'Anda mempunyai perubahan yang belum disimpan. Adakah anda pasti ingin membatalkan?',
      type: 'warning',
      confirmText: 'Ya, Batalkan',
      cancelText: 'Teruskan Edit',
    });
  }, [showConfirm]);

  const handleClose = () => {
    if (dialog.onCancel) {
      dialog.onCancel();
    }
  };

  const handleConfirm = () => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
  };

  const handleCancel = () => {
    if (dialog.onCancel) {
      dialog.onCancel();
    }
  };

  // Icon based on type
  const getIcon = () => {
    switch (dialog.type) {
      case 'danger':
        return <DeleteIcon sx={{ fontSize: 48, color: 'error.main' }} />;
      case 'warning':
        return <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 48, color: 'info.main' }} />;
      default:
        return <InfoIcon sx={{ fontSize: 48, color: 'info.main' }} />;
    }
  };

  // Button color based on type
  const getConfirmButtonColor = () => {
    switch (dialog.type) {
      case 'danger':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'primary';
      default:
        return 'primary';
    }
  };

  return (
    <ConfirmDialogContext.Provider
      value={{
        showConfirm,
        confirmDelete,
        confirmSave,
        confirmDiscard,
      }}
    >
      {children}
      <Dialog
        open={dialog.open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 24,
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {getIcon()}
            <span>{dialog.title}</span>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            component="div"
            sx={{
              whiteSpace: 'pre-line',
              color: 'text.primary',
              fontSize: '1rem',
            }}
          >
            {dialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2, gap: 1 }}>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={getConfirmButtonColor()}
            size="large"
            autoFocus
          >
            {dialog.confirmText}
          </Button>
          <Button
            onClick={handleCancel}
            variant="outlined"
            color="inherit"
            size="large"
          >
            {dialog.cancelText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmDialogContext.Provider>
  );
}

/**
 * Hook to access confirmation dialog functions
 * 
 * Usage in components:
 * const { showConfirm, confirmDelete, confirmSave, confirmDiscard } = useConfirmDialog();
 * 
 * Examples:
 * 
 * // Delete confirmation
 * const confirmed = await confirmDelete('akaun ini');
 * if (confirmed) {
 *   // Proceed with delete
 * }
 * 
 * // Save confirmation
 * const save = await confirmSave('Simpan maklumat penyewa?');
 * if (save) {
 *   // Save data
 * }
 * 
 * // Custom confirmation
 * const result = await showConfirm({
 *   title: 'Pengesahan Kustom',
 *   message: 'Mesej pengesahan anda',
 *   type: 'warning',
 *   confirmText: 'Teruskan',
 *   cancelText: 'Batal'
 * });
 */
export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return context;
}

ConfirmDialogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ConfirmDialogProvider;
